import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Envelope, PaperPlaneTilt, Plus, Pencil, Trash, LockKey } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import EnquiryDialog from "@/pages/spare-parts/components/EnquiryDialog";

// Sales statuses that lock the enquiry against workshop edits/deletes.
// Keep in sync with backend `_LOCKED_SALES_STATUSES` in
// `backend/routers/spares.py`.
const LOCKED_SALES_STATUSES = new Set(["contacted", "quoted", "won", "lost", "closed"]);

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);   // enquiry object or null
  const [deleting, setDeleting] = useState(null); // enquiry object or null
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/enquiries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const resubmit = async (id) => {
    try {
      const r = await api.post(`/enquiries/${id}/submit`);
      if (r.data.status === "submitted") toast.success("Sent");
      else toast.warning("Send failed — check logs");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete(`/spares/enquiries/${deleting.id}`);
      toast.success(`Enquiry ${deleting.enquiry_number} deleted`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const isLocked = (e) => LOCKED_SALES_STATUSES.has(e.sales_status || "");

  return (
    <div className="space-y-6" data-testid="enquiries-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase font-mono tracking-widest text-strawberry mb-1">Order Pipeline</div>
          <h1 className="text-3xl font-display font-bold">SparesHub Enquiries</h1>
          <p className="text-sm text-steel mt-2">
            Parts you&apos;ve asked SparesHub to source. Every enquiry is emailed to the SparesHub team the moment you submit it.
          </p>
        </div>
        <Button
          data-testid="enq-create-btn"
          className="h-11 px-5 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-bold inline-flex items-center gap-2 shadow"
          onClick={() => setCreating(true)}
        >
          <Plus size={16} weight="bold" /> Create Enquiry
        </Button>
      </div>

      <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
        <table className="w-full table-industrial">
          <thead>
            <tr><th>Enquiry #</th><th>Items</th><th>Urgency</th><th>Status</th><th>Sent</th><th>Job</th><th className="text-right pr-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-14 text-steel">
                <Envelope size={44} className="mx-auto text-steel/70 mb-3" weight="duotone" />
                <div className="text-[15px] font-semibold text-[#1d3557] mb-1">No enquiries yet</div>
                <div className="text-[13px] text-steel mb-4">Send your first parts enquiry to SparesHub — takes under a minute.</div>
                <Button
                  data-testid="enq-empty-create-btn"
                  className="h-10 px-4 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-bold inline-flex items-center gap-2"
                  onClick={() => setCreating(true)}
                >
                  <Plus size={15} weight="bold" /> Create your first enquiry
                </Button>
              </td></tr>
            )}
            {items.map((e) => {
              const locked = isLocked(e);
              return (
                <tr key={e.id} data-testid={`enq-row-${e.id}`}>
                  <td className="font-mono text-strawberry">{e.enquiry_number}</td>
                  <td className="text-sm">
                    {e.items.slice(0, 2).map((i) => i.part_name).join(", ")}
                    {e.items.length > 2 && ` +${e.items.length - 2} more`}
                  </td>
                  <td className="text-xs uppercase font-mono">{e.urgency}</td>
                  <td>
                    <span className={`inline-block text-[10px] uppercase font-mono tracking-widest px-2 py-1 border ${
                      e.status === "submitted" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" :
                      e.status === "failed" ? "bg-red-500/15 text-red-600 border-red-500/30" :
                      "bg-zinc-500/15 text-steel border-zinc-500/30"
                    }`}>{e.status}</span>
                  </td>
                  <td className="font-mono text-xs text-steel">{e.sent_at ? fmtDateTime(e.sent_at) : "—"}</td>
                  <td>{e.job_card_id ? <Link to={`/job-cards/${e.job_card_id}`} className="text-strawberry hover:underline text-xs">Open →</Link> : "—"}</td>
                  <td className="text-right pr-3">
                    <div className="inline-flex items-center gap-1">
                      {e.status !== "submitted" && !locked && (
                        <Button data-testid={`enq-resubmit-${e.id}`} size="sm" className="h-8 btn-primary-brand rounded-md text-white" onClick={() => resubmit(e.id)}>
                          <PaperPlaneTilt size={13} className="mr-1" weight="fill" /> Send
                        </Button>
                      )}
                      {locked ? (
                        <span
                          title={`SparesHub is processing this enquiry (${e.sales_status}). Contact them to make changes.`}
                          data-testid={`enq-locked-${e.id}`}
                          className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-[#E4E7EC] bg-[#F7F9FC] text-[11px] uppercase font-semibold text-[#667085]"
                        >
                          <LockKey size={12} weight="fill" /> Locked
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            data-testid={`enq-edit-${e.id}`}
                            onClick={() => setEditing(e)}
                            title="Edit enquiry"
                            className="w-8 h-8 rounded-md border border-[#E4E7EC] grid place-items-center text-[#457b9d] hover:bg-[#F1FAEE] hover:border-[#457b9d]/40"
                          >
                            <Pencil size={13} weight="bold" />
                          </button>
                          <button
                            type="button"
                            data-testid={`enq-delete-${e.id}`}
                            onClick={() => setDeleting(e)}
                            title="Delete enquiry"
                            className="w-8 h-8 rounded-md border border-[#E4E7EC] grid place-items-center text-[#a8dadc] hover:text-[#e63946] hover:border-[#e63946]/40"
                          >
                            <Trash size={13} weight="bold" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <EnquiryDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSubmitted={load}
        title="Submit Enquiry to SparesHub"
        contextLabel="Ad-hoc enquiry — not tied to a specific job card."
      />
      <EnquiryDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        onSubmitted={load}
        title="Edit Enquiry"
        contextLabel={editing ? `Editing ${editing.enquiry_number}` : ""}
        enquiry={editing}
      />

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => !busy && setDeleting(null)} data-testid="enq-delete-backdrop">
          <div onClick={(evt) => evt.stopPropagation()}
               className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
               data-testid="enq-delete-dialog">
            <div className="px-6 py-4 border-b border-[#E4E7EC]">
              <h3 className="text-[15px] font-bold text-[#1d3557]">Delete enquiry {deleting.enquiry_number}?</h3>
              <p className="text-[12.5px] text-[#667085] mt-1">
                This will permanently remove the enquiry from your workshop. SparesHub will lose visibility of it.
                {deleting.items?.length ? ` (${deleting.items.length} part${deleting.items.length === 1 ? "" : "s"})` : ""}
              </p>
            </div>
            <div className="px-6 py-3 bg-[#F7F9FC] flex justify-end gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setDeleting(null)}
                className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white disabled:opacity-50"
                data-testid="enq-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={confirmDelete}
                className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="enq-delete-confirm"
              >
                <Trash size={13} weight="bold" /> {busy ? "Deleting…" : "Delete enquiry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
